import {
	Paper,
	SortDirection,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { visuallyHidden } from "@mui/utils"
import React, { MouseEvent } from "react"
import { useSelector } from "react-redux"

import { RootState } from "../../../store"
import {
	CampaignsPaginatedListState,
	CampaignsState,
} from "../../../store/actions/campaigns"
import NoData from "../../error/nodata"
import CampaignRow from "./row"

interface IEnhancedCorporateTable {
	sortCriteria: [{ field: string; order: SortDirection }]
	setSortCriteria: (value: { field: string; order: SortDirection }[]) => void
}

interface IEnhancedTableHeadProps {
	sortCriteria: [{ field: string; order: SortDirection }]
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof CampaignsState
	) => void
}

interface HeadCell {
	id: keyof CampaignsState
	label: string
	numeric: boolean
	disablePadding: boolean
}

const TableCellStyled = styled(TableCell)(() => ({
	paddingLeft: "8px",
}))

const TableHeadStyled = styled(TableHead)(() => ({
	backgroundColor: "#E9EDF0",
}))

const headCells: HeadCell[] = [
	{ id: "campaignName", numeric: false, disablePadding: false, label: "Nome" },
	{
		id: "authorizationType",
		numeric: false,
		disablePadding: false,
		label: "Tipo autorizzazione",
	},
	{
		id: "enabled",
		numeric: false,
		disablePadding: false,
		label: "Abilitato",
	},
	{
		id: "deleted",
		numeric: false,
		disablePadding: false,
		label: "Cancellato",
	},
	{
		id: "campaignLogo",
		numeric: false,
		disablePadding: false,
		label: "Logo",
	},
	{ id: "idLogo", numeric: false, disablePadding: false, label: "UUID logo" },
]

function EnhancedTableHead(props: IEnhancedTableHeadProps) {
	const { sortCriteria, onRequestSort } = props
	const createSortHandler =
		(property: keyof CampaignsState) => (event: MouseEvent<unknown>) => {
			onRequestSort(event, property)
		}

	return (
		<TableHeadStyled>
			<TableRow>
				<TableCellStyled />
				{headCells.map((headCell) => {
					const cellSorted:
						| { field: string; order: SortDirection }
						| undefined = sortCriteria.find(
						(criteria: { field: string; order: SortDirection }) =>
							criteria.field === headCell.id
					)
					return (
						<TableCellStyled
							key={headCell.id}
							align={headCell.numeric ? "right" : "left"}
							padding={headCell.disablePadding ? "none" : "normal"}
							sortDirection={!!cellSorted ? cellSorted.order : undefined}
						>
							<TableSortLabel
								active={!!cellSorted}
								direction={!!cellSorted ? cellSorted.order : "asc"}
								onClick={createSortHandler(headCell.id)}
							>
								{headCell.label}{" "}
								{!!cellSorted ? (
									<span style={visuallyHidden}>
										{" "}
										{cellSorted.order === "desc"
											? "sorted descending"
											: "sorted ascending"}{" "}
									</span>
								) : null}
							</TableSortLabel>
						</TableCellStyled>
					)
				})}
				<TableCellStyled />
			</TableRow>
		</TableHeadStyled>
	)
}

const CampaignsEnhancedTable = ({
	sortCriteria,
	setSortCriteria,
}: IEnhancedCorporateTable) => {
	const campaignsState: CampaignsPaginatedListState = useSelector(
		(state: RootState) => state.campaignsPaginated
	)

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof CampaignsState
	) => {
		const cellSorted: { field: string; order: SortDirection } | undefined =
			sortCriteria.find(
				(criteria: { field: string; order: SortDirection }) =>
					criteria.field === property
			)
		const isAsc = !!cellSorted ? cellSorted.order : false
		setSortCriteria([{ field: property, order: isAsc ? "desc" : "asc" }])
	}

	return (
		<TableContainer component={Paper} sx={{ marginTop: "16px" }}>
			<Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
				<EnhancedTableHead
					sortCriteria={sortCriteria}
					onRequestSort={handleRequestSort}
				/>
				<TableBody>
					{campaignsState?.list?.page?.content &&
						campaignsState.list.page.content.map((row: CampaignsState) => (
							<CampaignRow row={row} />
						))}

					{campaignsState?.list?.page?.content?.length === 0 && (
						<TableRow>
							<TableCellStyled
								colSpan={9}
								style={{ textAlign: "center" }}
								component="td"
								scope="row"
								padding="none"
							>
								<NoData />
							</TableCellStyled>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	)
}
export default CampaignsEnhancedTable
